import clsx from "clsx";
import { Component, type ParentComponent, Show, createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import { InterstitialGeneratingViewer } from "~/components/chat/messages/r/InterstitialGeneratingViewer";
import { InterstitialViewer } from "~/components/chat/messages/r/InterstitialViewer";
import { ExtendedPaneArtifactViewer } from "./ExtendedPaneArtifactViewer";
import {
  ExtendedPaneContentType,
  ExtendedPaneMode,
  NavigationPaneMode,
  isExtendedPaneVisible,
  useAppUI,
} from "./AppUIContext";

export const ExtendedPane: ParentComponent = (props) => {
  const { state, actions } = useAppUI();
  const [isResizing, setIsResizing] = createSignal(false);

  let resizeHandleRef: HTMLDivElement | undefined;

  // Auto-size to 50% of available space when first opened or viewport changes
  createEffect(() => {
    if (isServer || isResizing()) return; // Don't auto-resize during manual resize

    const handleResize = () => {
      if (isExtendedPaneVisible(state.extendedPane) && !isResizing() && !state.extendedPane.hasBeenManuallyResized) {
        const navWidth = state.navigationPane.mode === NavigationPaneMode.DOCKED ? state.navigationPane.width : 0;
        const availableWidth = window.innerWidth - navWidth;
        const targetWidth = availableWidth * 0.5;

        // Only auto-resize if current width is significantly different from 50%
        const currentWidth = state.extendedPane.width;
        const widthDifference = Math.abs(currentWidth - targetWidth);

        // Update if difference is more than 50px to avoid constant adjustments
        if (widthDifference > 50) {
          actions.setExtendedPaneWidth(targetWidth);
        }
      }
    };

    // Initial sizing
    handleResize();

    // Listen for window resize
    window.addEventListener("resize", handleResize);

    onCleanup(() => {
      window.removeEventListener("resize", handleResize);
    });
  });

  // Resize functionality
  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = state.extendedPane.width;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = startWidth - deltaX; // Right side panel, dragging left (negative deltaX) increases width, dragging right (positive deltaX) decreases width

      // Apply width change immediately without batching for responsive feel
      requestAnimationFrame(() => {
        actions.setExtendedPaneWidth(newWidth, true); // Mark as manual resize
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove, { capture: true });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    };

    document.addEventListener("mousemove", handleMouseMove, { capture: true });
    document.addEventListener("mouseup", handleMouseUp, { capture: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.body.style.pointerEvents = "none"; // Prevent interference from other elements
  };

  const handleToggle = () => {
    actions.toggleExtendedPane();
  };

  const isVisible = () => isExtendedPaneVisible(state.extendedPane);
  const paneWidth = () => state.extendedPane.width;

  const activeContent = createMemo(() => {
    // Show the single active artifact if available
    return state.extendedPane.activeArtifact;
  });

  // Render the appropriate viewer based on content type
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const renderViewer = (content: any) => {
    if (!content) return null;

    const contentType = content.type || ExtendedPaneContentType.ARTIFACT; // Default to artifact for backwards compatibility

    switch (contentType) {
      case ExtendedPaneContentType.INTERSTITIAL:
        return <InterstitialViewer operationId={content.operationId} blockId={content.blockId} chat={content.chat} />;
      case ExtendedPaneContentType.INTERSTITIAL_GENERATING:
        return (
          <InterstitialGeneratingViewer
            operationId={content.operationId}
            blockId={content.blockId}
            chat={content.chat}
          />
        );
      case ExtendedPaneContentType.ARTIFACT:
        return (
          <ExtendedPaneArtifactViewer
            operationId={content.operationId}
            blockId={content.blockId}
            chat={content.chat}
            title={content.title}
          />
        );
      default:
        return (
          <ExtendedPaneArtifactViewer
            operationId={content.operationId}
            blockId={content.blockId}
            chat={content.chat}
            title={content.title}
          />
        );
    }
  };

  return (
    <Show when={isVisible()}>
      <aside
        class="fixed top-0 h-screen flex flex-col bg-canvas"
        style={{
          width: `${paneWidth()}px`,
          right: "0px",
        }}
        aria-label="Extended pane"
      >
        {/* Simplified content area - no header, no borders */}
        <div class="flex-1 h-full">
          {(() => {
            // Priority order: children > active content > static content > fallback
            if (props.children) {
              return props.children;
            }

            const content = activeContent();
            if (content) {
              return renderViewer(content);
            }

            if (state.extendedPane.content) {
              return state.extendedPane.content;
            }

            return (
              <div class="text-center py-8">
                <div class="text-secondary-text">
                  <p class="text-sm">No content to display</p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Resize handle */}
        <div
          ref={resizeHandleRef}
          class={clsx(
            "absolute top-0 h-full w-2 cursor-col-resize transition-colors group -left-3",
            isResizing() ? "bg-brand/20" : "bg-transparent hover:bg-brand/10",
          )}
          onMouseDown={handleResizeStart}
          aria-label="Resize extended pane"
        >
          {/* Border highlight */}
          <div
            class={clsx(
              "absolute top-0 h-full w-1 transition-colors left-2",
              isResizing() ? "bg-brand" : "bg-transparent group-hover:bg-brand/50",
            )}
          />
          {/* Visual drag indicator */}
          <div
            class={clsx(
              "absolute top-1/2 -translate-y-1/2 w-1 h-20 rounded-full transition-colors left-2",
              isResizing() ? "bg-brand" : "bg-primary-border group-hover:bg-brand",
            )}
          />
        </div>
      </aside>
    </Show>
  );
};
